import { Grid } from '@mui/material'
import React from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import TransformDisplayToEdition from '../../../../components/form/TransformDisplayToEdition'
import { displayedFieldCompanionAlignSx } from '../../../../components/form/DisplayedItem'
import { displayAsCurrency } from '../../../../utils/displayAsCurrency'
import mapEnumForSelect from '../../../../utils/mapEnumForSelect'
import { AdGroupCreateInput, AdGroupInput, BudgetCapping as BudgetCappingEnum } from '../../../../__generated__/graphql'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { FieldsProps } from '.'
import { useFormikContext } from 'formik'
import { FieldGroup, fieldGroupItemSize } from './FieldGroup'
import OverallBudgetCalculatorButton from './OverallBudgetCalculatorButton'
import { AdgroupBudgetUserPermissions, useAdgroupBudgetUserPermissions } from '../../../company/userPermissions'
import {
  filterEnumOptionsByPermission,
  isAdgroupBudgetCappingEnabled,
  isBudgetCappingTypeAllowed,
} from './adgroupBudgetPermissions.utils'

export interface BudgetCappingProps {
  adgroup: AdGroupInput | AdGroupCreateInput
}

const BudgetCapping: React.FC<
  BudgetCappingProps &
    Pick<FieldsProps, 'isWizard' | 'canEdit' | 'fullWidth' | 'allowUnchanged'> & {
      budgetPermissions?: AdgroupBudgetUserPermissions
    }
> = ({
  adgroup,
  isWizard = false,
  canEdit = true,
  fullWidth = false,
  allowUnchanged = false,
  budgetPermissions,
}): JSX.Element => {
  const t = useAppTranslations()
  const userPermissions = useAdgroupBudgetUserPermissions()
  const permissions = budgetPermissions ?? userPermissions
  const {
    values: { budgetCappingType },
  } = useFormikContext<{ budgetCappingType: BudgetCappingEnum | '' }>()

  if (!isAdgroupBudgetCappingEnabled(permissions)) return <></>

  const budgetCappingTypeOptions = filterEnumOptionsByPermission(
    mapEnumForSelect(BudgetCappingEnum).map((item) => ({
      value: item.value,
      label: t(`enum.BudgetCapping.${item.label}`),
    })),
    (type) => isBudgetCappingTypeAllowed(type, permissions),
    budgetCappingType || undefined
  )

  return (
    <FieldGroup size={fullWidth ? (allowUnchanged ? 6 : 12) : 4}>
      <Grid size={fieldGroupItemSize}>
        <TransformDisplayToEdition
          inputType="select"
          withNone={allowUnchanged}
          noneTranslation="adgroup.budgetAndDates.budgetPlans.unchanged"
          name="budgetCappingType"
          required={!allowUnchanged}
          label={t('adgroup.budgetAndDates.budgetCappingType')}
          tooltipText="adgroup.budgetAndDates.budgetCappingTypeHelper"
          text={budgetCappingType}
          disableHoverEdition={isWizard}
          editable={canEdit}
          options={budgetCappingTypeOptions}
        />
      </Grid>
      {budgetCappingType !== BudgetCappingEnum.Open && (
        <Grid size={fieldGroupItemSize}>
          <Grid container direction={{ md: 'row', xs: 'column' }} spacing={1} wrap="nowrap" alignItems="flex-start">
            <Grid size="grow">
              <TransformDisplayToEdition
                inputType="input"
                type="number"
                name="budgetCapping"
                required={!allowUnchanged}
                label={t('adgroup.budgetAndDates.budgetCapping')}
                text={displayAsCurrency(adgroup.budgetCapping)}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                tooltipText="adgroup.budgetAndDates.budgetCappingHelper"
                disableHoverEdition={isWizard}
                editable={canEdit}
              />
            </Grid>
            {!isWizard && (
              <Grid size="auto" sx={displayedFieldCompanionAlignSx}>
                <OverallBudgetCalculatorButton disabled={!canEdit} />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </FieldGroup>
  )
}
export default BudgetCapping
